/**
 * THE SCRUBBER — the one place anything decides what may leave this process
 * (APP-MONITORING M0.4, M1.4; DATA-GOVERNANCE §1, §8).
 *
 * WHY ALLOW-LIST AND NOT DENY-LIST. A deny-list is a list of the leaks someone
 * already thought of; every field added later is shipped by default and
 * discovered by an incident. An allow-list inverts the failure mode: a field
 * nobody has named is DROPPED, so the cost of forgetting is a missing
 * diagnostic — noticed by the engineer who wanted it, in minutes — instead of
 * patient data in a vendor's index, noticed by nobody. The requirement states
 * this as the acceptance sentence: *given a developer adds a field to any
 * telemetry payload, the field is dropped until the allow-list names it — the
 * failure mode is silence, not leakage.*
 *
 * FOUR SINKS, DIFFERENT RULES, ONE MODULE. They differ in WHERE the data comes
 * to rest, which is the only thing that changes what may travel:
 *
 *   crash  — Sentry. Leaves India, reaches a third-party processor. The
 *            strictest: no session or episode id (DEC-2), no free text ever.
 *   usage  — first-party `usage_events` in Postgres, India (DEC-4). Session
 *            and episode ids allowed; still no free text.
 *   log    — the API's own log lines → CloudWatch, India. Same shape as usage.
 *   report — problem reports in Postgres, India. The ONLY sink that accepts
 *            free text, because a report without the person's own words is not
 *            a report. That text is PHI-capable by assumption and is handled
 *            as such downstream (excluded from exports, never forwarded to
 *            Sentry) — the protection is that it never reaches the crash sink,
 *            not that it is filtered here.
 *
 * WHAT THIS MODULE IS NOT. It is not a PHI detector. There is no reliable way
 * to recognise a patient's name in a string, and a module that pretended to
 * would invite exactly the deny-list thinking above. Names are kept out by the
 * allow-list never naming a key that holds one — not by inspecting values. The
 * value rules below exist only for data whose SHAPE is unmistakable (a JWT, an
 * e-mail, an IP, a link token, a study code) and which therefore should never
 * survive even inside an allow-listed field.
 */

import { maskToken } from './logging';

export const TELEMETRY_SINKS = ['crash', 'usage', 'report', 'log'] as const;
export type TelemetrySink = (typeof TELEMETRY_SINKS)[number];

/**
 * Context key → the name that key travels under as a Sentry TAG, where the
 * two deliberately differ (F8/F9, Sameer 2026-09-07).
 *
 * `inCall` is camelCase because every other context key here is (`appRunId`,
 * `pathTemplate`, `clockOffsetMs`). The TAG is an external contract: the
 * increment-11 plan §1 K-PR6, ADR-0024 §9 and RUNBOOK "Alerts and response"
 * all specify `in_call`, and the Sentry rule that pages an operator for a
 * mid-call crash filters that literal string. A tag emitted as `inCall` would
 * satisfy every test in this repository and never fire the one alert it
 * exists for.
 *
 * It lives HERE, next to the allow-list, because the allow-list must name a
 * key as it appears ON THE WIRE. Tags are renamed before `beforeSend` sees
 * them, so an allow-list that knew only `inCall` would drop the very tag the
 * rename exists to produce — which is exactly what the first draft of this
 * fix did. Deriving the allow-list from this map below makes the two
 * incapable of drifting apart.
 */
export const CRASH_TAG_NAMES: Readonly<Record<string, string>> = {
  inCall: 'in_call',
};

/** The Sentry tag name for a context key. Identity unless the map says otherwise. */
export function crashTagName(contextKey: string): string {
  return CRASH_TAG_NAMES[contextKey] ?? contextKey;
}

/**
 * Context every sink may carry (M1.3). Each of these identifies a BUILD, a
 * DEVICE, a RUN or a REQUEST — never a person and never a clinical record.
 */
const COMMON_KEYS = [
  // Release identity (M0.1) — which code produced this.
  'release',
  'instance',
  'appVersion',
  'buildNumber',
  'buildSha',
  // Correlation (M0.3) — which run, which request.
  'appRunId',
  'requestId',
  'reqId',
  // The app's own event id for one captured error (M2.7). It is minted by
  // `telemetry.captureError`, shown to the physio on the recovery screen and
  // carried by a problem report's `crashEventIds` — so it is the join between
  // a report and its Sentry issue, and it has to survive to be searchable as a
  // tag. A UUIDv7 of our own minting: it names an EVENT, never a person or a
  // record. It was missing from this list until 2026-09-08, which meant
  // `beforeSend` deleted the tag on every crash and the join could not work.
  'appEventId',
  // Who, as a pseudonym only (M0.5). Never an e-mail, never a Supabase sub.
  'clinicianPseudonym',
  // Device and platform.
  'device',
  'os',
  'osVersion',
  'networkState',
  'permissions',
  'freeMemoryBytes',
  'freeStorageBytes',
  'clockOffsetMs',
  // Where in the app, by NAME only — never route params (M1.3).
  'screen',
  'inCall',
  // Shape of an API call: the template, never the concrete path (M2.3).
  'method',
  'pathTemplate',
  'status',
  'statusCode',
  'durationMs',
  'responseTime',
  // Our own error vocabulary — codes and template messages (M0.6).
  //
  // Deliberately NOT a bare `name`: it is the single likeliest key in this
  // codebase to hold a person's, and an allow-list entry that is *usually*
  // an error class and *sometimes* a patient is not an allow-list entry. An
  // error's class name travels as `errorName`.
  'code',
  'level',
  'msg',
  'errorName',
  'timestamp',
  'time',
  // Live-call diagnostics: numbers from the pipeline, no landmarks (M1.3).
  'poseSnapshot',
  'agoraState',
  'uploadQueueDepth',
  'fps',
  'droppedFrames',
  'latencyMs',
  'bitrateKbps',
  'confidence',
  'attempts',
  'pendingParts',
  'signedIn',
] as const;

/**
 * Identifiers of clinical records. They name a real episode of care, so they
 * are PHI-adjacent (DATA-GOVERNANCE §1) and may travel only to sinks that stay
 * in India. DEC-2 keeps them out of crash events specifically: a Sentry issue
 * carrying a session id would let a vendor's index join a stack trace to a
 * course of treatment.
 */
const INDIA_ONLY_KEYS = ['sessionId', 'episodeId', 'patientId'] as const;

/**
 * Connection metadata — the client IP and port (DEC-11, option B).
 *
 * This is the one class of data the requirement deliberately KEEPS rather than
 * scrubs, and only on the `log` sink. DEC-11 weighed exactly this and rejected
 * "stop logging patient IPs" by name, because it "gives up the ability to
 * investigate abuse on the join path". So the honest treatment is not to
 * remove it but to classify it: its own DATA-GOVERNANCE §1 row, 30 days,
 * security and operations only (M0.9).
 *
 * It stays out of `crash` (that sink leaves India and reaches a processor),
 * out of `usage` and out of `report`. A scrubber that silently dropped it from
 * the logs too would look safer while quietly overriding a recorded decision.
 */
const CONNECTION_METADATA_KEYS = ['remoteAddress', 'remotePort'] as const;

/**
 * Keys that hold prose a human typed. Allowed on `report` and nowhere else —
 * a person describing a problem may name anything at all, which is exactly why
 * their words must not ride a crash event to a third party.
 */
const FREE_TEXT_KEYS = [
  'text',
  'freeText',
  'note',
  'notes',
  'comment',
  'description',
  'body',
  'detail',
] as const;

/** Report-only structured fields that accompany the person's own words. */
const REPORT_KEYS = ['category', 'severity', 'crashEventIds', 'breadcrumbs', 'occurredAt'] as const;

/**
 * Every renamed key must be allow-listed under BOTH spellings: the context
 * form a caller writes, and the wire form a sink actually carries. Derived,
 * never typed out twice — see CRASH_TAG_NAMES.
 */
const WIRE_NAMES = Object.values(CRASH_TAG_NAMES);

const ALLOW_LISTS: Readonly<Record<TelemetrySink, ReadonlySet<string>>> = {
  crash: new Set<string>([...COMMON_KEYS, ...WIRE_NAMES]),
  usage: new Set<string>([
    ...COMMON_KEYS,
    ...WIRE_NAMES,
    ...INDIA_ONLY_KEYS,
    'event',
    'properties',
  ]),
  log: new Set<string>([
    ...COMMON_KEYS,
    ...WIRE_NAMES,
    ...INDIA_ONLY_KEYS,
    ...CONNECTION_METADATA_KEYS,
    'req',
    'res',
    'url',
    'hostname',
  ]),
  report: new Set<string>([
    ...COMMON_KEYS,
    ...WIRE_NAMES,
    ...INDIA_ONLY_KEYS,
    ...FREE_TEXT_KEYS,
    ...REPORT_KEYS,
  ]),
};

/** Only `report` may carry prose. */
const FREE_TEXT_ALLOWED: Readonly<Record<TelemetrySink, boolean>> = {
  crash: false,
  usage: false,
  log: false,
  report: true,
};

/**
 * A string this long in a telemetry field is prose, whatever its key claims to
 * be. The cap is a backstop for a field that was allow-listed for short enum
 * values and one day receives a sentence — the allow-list is the primary
 * defence, this is the one behind it.
 */
const MAX_VALUE_LENGTH = 200;

/** Deepest object we walk. Beyond this a payload is dropped, not flattened. */
const MAX_DEPTH = 8;

// --- value rules ----------------------------------------------------------
// Each matches a SHAPE that is unmistakable. Nothing here guesses at meaning.

/** `a.b.c` in base64url — a JWT or refresh token (M1.4). */
const JWT = /\b[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/;
/** An e-mail address — never permitted on any sink (M1.4). */
const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
/** IPv4, and the common IPv6 forms. Connection metadata, never telemetry (M1.4). */
const IPV4 = /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/;
const IPV6 = /\b(?:[0-9a-f]{1,4}:){2,7}[0-9a-f]{1,4}\b/i;
/** The study's own pseudonym for a patient. Appears nowhere in telemetry (M0.5). */
const STUDY_CODE = /\bPatient-\d{1,6}\b/i;
/** `Authorization: Bearer …` in any casing. */
const BEARER = /\bBearer\s+([A-Za-z0-9._~+/-]+=*)/i;
/** The patient link token's own route (TM-02) — the existing redaction, reused. */
const LINK_TOKEN_SEGMENT = /(\/api\/link\/)[^/?#]+/g;
/**
 * The SAME token, on patient-web's OWN route rather than the API's
 * (`/join/<token>`, apps/patient-web/src/api.ts's `linkTokenFromPath`). A
 * patient's page URL carries the token directly in its path — the one place
 * `LINK_TOKEN_SEGMENT` above does not reach, since that pattern was written
 * for the API's request path, not the browser's own location. Telemetry that
 * ever carries `window.location.href` (a default browser-SDK breadcrumb, an
 * error message that echoes the current URL) needs this redaction too.
 */
const PATIENT_JOIN_SEGMENT = /(\/join\/)[^/?#]+/g;
/** A UUID sitting in a path — a concrete id where a template belongs. */
const UUID_SEGMENT = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
/** Looks like a URL or an absolute path — the things path rules apply to. */
const URLISH = /^(?:[a-z][a-z0-9+.-]*:\/\/|\/)/i;

/** The marker left where a value was removed, so a reader sees a hole, not an absence. */
export const DROPPED = '[dropped]';

/**
 * Reduce a URL or path to its template: no query string (M1.4), no link token
 * (TM-02), and no concrete ids on cross-border sinks.
 * `/api/link/abc?x=1` → `/api/link/[redacted]`.
 *
 * F7: On the `log` sink (which stays in India and allow-lists clinical record
 * identifiers like `sessionId`), UUID path segments are PRESERVED so an
 * operator investigating an issue can grep the service logs for a concrete
 * session id. On `crash`, `usage` and `report`, UUIDs are templated to `/:id`.
 */
export function scrubUrl(value: string, sink?: TelemetrySink): string {
  const withoutQuery = value.split(/[?#]/)[0] ?? '';
  const withoutToken = withoutQuery
    .replace(LINK_TOKEN_SEGMENT, '$1[redacted]')
    .replace(PATIENT_JOIN_SEGMENT, '$1[redacted]');
  if (sink === 'log') {
    return withoutToken;
  }
  return withoutToken.replace(UUID_SEGMENT, '/:id');
}

/**
 * Apply the value rules to one string. Returns `null` when the value must not
 * survive at all — the caller drops the key rather than writing an empty
 * string, so a reader can tell "removed" from "was blank".
 */
export function scrubString(value: string, sink: TelemetrySink): string | null {
  // Unmistakable credentials and identifiers first: these never survive, on
  // any sink, in any field — including `report`, because a token in a
  // person's prose is still a live credential.
  if (EMAIL.test(value)) return null;
  if (STUDY_CODE.test(value)) return null;
  if (JWT.test(value)) return null;

  // The one exception, and it is a decision rather than an oversight: client
  // IPs stay in the API's own logs as connection metadata (DEC-11, above).
  // Everywhere else an IP is dropped wherever it appears — including inside
  // an allow-listed string, which is how one usually arrives.
  if (sink !== 'log' && (IPV4.test(value) || IPV6.test(value))) return null;

  let out = value;
  const bearer = BEARER.exec(out);
  if (bearer?.[1]) out = out.replace(bearer[1], maskToken(bearer[1]));
  if (URLISH.test(out)) out = scrubUrl(out, sink);

  // Prose: allowed only where the requirement says a human's own words belong.
  if (!FREE_TEXT_ALLOWED[sink] && out.length > MAX_VALUE_LENGTH) return null;
  return out;
}

/**
 * A literal object, and nothing else.
 *
 * The prototype check is the whole point: `typeof x === 'object'` is also true
 * of every class instance, an Error, a Date and a Map. Walking those as if
 * they were payloads would treat fields nobody reviewed as if they had been —
 * a `new Patient()` whose `name` happened to collide with an allow-listed key
 * would sail straight through. Anything that is not a plain object is dropped
 * rather than inspected.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

/**
 * Scrub one payload for one sink.
 *
 * Keys not on the sink's allow-list are dropped — at every level, with the
 * same list, so a forbidden key cannot be smuggled in by nesting it. Values
 * are then passed through the rules above. The result is a NEW object: the
 * caller's payload is never mutated, because a scrubber that edited its input
 * would silently change what the app itself then goes on to use.
 */
export function scrub(payload: unknown, sink: TelemetrySink, depth = 0): unknown {
  if (depth > MAX_DEPTH) return undefined;

  if (typeof payload === 'string') return scrubString(payload, sink) ?? undefined;
  if (typeof payload === 'number' || typeof payload === 'boolean') return payload;
  if (payload === null || payload === undefined) return undefined;

  if (Array.isArray(payload)) {
    return payload.map((item) => scrub(item, sink, depth + 1)).filter((v) => v !== undefined);
  }

  if (!isPlainObject(payload)) {
    // Dates, Errors, class instances, functions: not a shape whose keys were
    // ever reviewed, so nothing here is allow-listed by name.
    return undefined;
  }

  const allowed = ALLOW_LISTS[sink];
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (!allowed.has(key)) continue;
    // The named free-text keys are on the report allow-list only; this is the
    // second half of the same rule, for a key that appears on both lists.
    if (!FREE_TEXT_ALLOWED[sink] && (FREE_TEXT_KEYS as readonly string[]).includes(key)) continue;

    const scrubbed = scrub(value, sink, depth + 1);
    if (scrubbed !== undefined) out[key] = scrubbed;
  }
  return out;
}

/** The keys a sink accepts — exported so a payload builder can be tested against it. */
export function allowedKeys(sink: TelemetrySink): ReadonlySet<string> {
  return ALLOW_LISTS[sink];
}
